/**
 * Children Comparison Card - Enhanced Version
 * Shows comprehensive children compliance with grouped form categories
 * Summary + Problem-Focused view for scalability
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import {
  Baby, CheckCircle2, XCircle, AlertTriangle, 
  ChevronDown, ChevronUp, Printer, RefreshCw, FileText, Heart, FileCheck, Mail, Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChildrenComparisonCard = ({ inspectorEmail }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedChildren, setSelectedChildren] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchChildrenCompliance();
  }, []);

  const fetchChildrenCompliance = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/compliance/children-full-compliance`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch children compliance:", error);
      toast.error("Failed to load children compliance data");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'enrollment': return <FileText className="w-4 h-4" />;
      case 'health': return <Heart className="w-4 h-4" />;
      case 'consent': return <FileCheck className="w-4 h-4" />;
      default: return null;
    }
  };

  const getCategoryColor = (percentage) => {
    if (percentage >= 90) return "text-green-600";
    if (percentage >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  // Toggle all children selection
  const toggleAllChildren = (selectAll) => {
    if (!data?.childrenNeedingAttention) return;
    const newSelected = {};
    data.childrenNeedingAttention.forEach(child => {
      newSelected[child.childId] = selectAll;
    });
    setSelectedChildren(newSelected);
  };

  // View child's uploaded documents
  const viewChildDocuments = async (childId, childName) => {
    try {
      const response = await axios.get(`${API}/children-records/${childId}/form-documents`);
      const docs = response.data.documents || {};
      
      if (Object.keys(docs).length === 0) {
        toast.info(`No documents uploaded for ${childName}`);
        return;
      }
      
      // Open documents in new window
      const docEntries = Object.entries(docs);
      for (const [formId, doc] of docEntries) {
        if (doc.fileData) {
          // Convert base64 to blob and open
          const byteCharacters = atob(doc.fileData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: doc.mimeType || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
        }
      }
      toast.success(`Opened ${docEntries.length} document(s) for ${childName}`);
    } catch (error) {
      console.error("Failed to view documents:", error);
      toast.error("Failed to load documents");
    }
  };

  // Send selected children records to inspector
  const sendSelectedChildrenRecords = async () => {
    if (!inspectorEmail || !inspectorEmail.includes('@')) {
      toast.error("Please enter inspector email at the bottom of the page first");
      return;
    }
    
    const selectedCount = Object.values(selectedChildren).filter(Boolean).length;
    if (selectedCount === 0) {
      toast.error("Please select at least one child to email");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/compliance/send-inspection-report`, {
        inspectorEmail,
        includeStaffReport: false,
        includeChildrenReport: true,
        includeFacilityReport: false,
        includeWalkthroughReport: false,
        includeEmergencyCards: false,
        isIndividualReport: true,
        reportType: 'children',
        selectedItems: selectedChildren
      });
      
      if (response.data.success) {
        const count = response.data.attachmentCount || selectedCount;
        toast.success(`${count} children document(s) sent as attachments to ${inspectorEmail}!`);
      } else {
        toast.error("Failed to send email");
      }
    } catch (error) {
      console.error("Failed to send children records:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper to get selected children documents for preview
  const getSelectedChildrenDocsList = () => {
    if (!data?.children) return [];
    const selectedIds = Object.entries(selectedChildren)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    const docsList = [];
    selectedIds.forEach(childId => {
      const child = data.children.find(c => c.id === childId);
      if (child) {
        // Get forms that have documents
        const forms = child.forms || {};
        Object.entries(forms).forEach(([formId, formData]) => {
          if (formData?.hasDocument) {
            docsList.push({
              childName: child.name,
              fileName: formData.fileName || `${formId.replace('_', ' ')} form`,
              formType: formId.replace('_', ' ')
            });
          }
        });
        if (docsList.filter(d => d.childName === child.name).length === 0) {
          docsList.push({
            childName: child.name,
            fileName: "(form documents from records)",
            formType: "Children Records"
          });
        }
      }
    });
    return docsList;
  };

  const printChildrenReport = () => {
    if (!data) return;
    
    setPrinting(true);
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Children Records Compliance Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          .summary { background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center; }
          .summary-item { padding: 10px; }
          .summary-value { font-size: 24px; font-weight: bold; color: #1a365d; }
          .summary-label { font-size: 12px; color: #666; }
          .category-section { margin: 20px 0; padding: 15px; background: #fff; border: 1px solid #ddd; border-radius: 8px; }
          .category-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
          .category-title { font-size: 16px; font-weight: bold; color: #1a365d; }
          .progress-bar { height: 8px; background: #e5e7eb; border-radius: 4px; overflow: hidden; }
          .progress-fill { height: 100%; background: #28a745; }
          .attention-section { margin-top: 30px; }
          .attention-item { padding: 10px; border-bottom: 1px solid #eee; }
          .child-name { font-weight: bold; color: #1a365d; }
          .child-category { color: #666; font-size: 11px; background: #f0f0f0; padding: 2px 6px; border-radius: 3px; }
          .missing-items { color: #dc3545; font-size: 12px; }
          .expired-items { color: #fd7e14; font-size: 12px; }
          .expiring-items { color: #ffc107; font-size: 12px; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 15px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Children Records Compliance Report</h1>
        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-value">${data.totalChildren}</div>
              <div class="summary-label">Total Children</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${data.completeItems}</div>
              <div class="summary-label">Forms Complete</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${data.totalItems - data.completeItems}</div>
              <div class="summary-label">Forms Needed</div>
            </div>
            <div class="summary-item">
              <div class="summary-value" style="color: ${data.overallPercentage >= 90 ? '#28a745' : data.overallPercentage >= 70 ? '#ffc107' : '#dc3545'}">${data.overallPercentage}%</div>
              <div class="summary-label">Overall Compliance</div>
            </div>
          </div>
        </div>
        
        ${Object.entries(data.categories).map(([key, cat]) => `
          <div class="category-section">
            <div class="category-header">
              <span class="category-title">${cat.label} (${cat.items.length} forms)</span>
              <span style="color: ${cat.percentage >= 90 ? '#28a745' : cat.percentage >= 70 ? '#ffc107' : '#dc3545'}; font-weight: bold;">
                ${cat.complete}/${cat.total} (${cat.percentage}%)
              </span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${cat.percentage}%; background: ${cat.percentage >= 90 ? '#28a745' : cat.percentage >= 70 ? '#ffc107' : '#dc3545'}"></div>
            </div>
            <p style="font-size: 11px; color: #666; margin-top: 5px;">Includes: ${cat.items.join(', ')}</p>
          </div>
        `).join('')}
        
        ${data.childrenNeedingAttention.length > 0 ? `
          <div class="attention-section">
            <h2 style="color: #dc3545;">⚠️ Children Needing Attention (${data.totalNeedingAttention})</h2>
            ${data.childrenNeedingAttention.map(child => `
              <div class="attention-item">
                <span class="child-name">${child.name}</span>
                <span class="child-category">${child.category || 'Enrolled'}</span>
                ${child.missing.length > 0 ? `<div class="missing-items">Missing: ${child.missing.join(', ')}</div>` : ''}
                ${(child.expired || []).length > 0 ? `<div class="expired-items">Expired: ${child.expired.join(', ')}</div>` : ''}
                ${child.expiring.length > 0 ? `<div class="expiring-items">Expiring: ${child.expiring.join(', ')}</div>` : ''}
              </div>
            `).join('')}
          </div>
        ` : '<p style="color: #28a745; text-align: center; margin-top: 20px;">✓ All children records are complete!</p>'}
        
        <div class="footer">
          <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          <p>This report shows comprehensive children records compliance across enrollment, health, and consent forms.</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
    setPrinting(false);
  };

  if (loading) {
    return (
      <Card className="bg-white border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Baby className="w-5 h-5 text-[#1a365d]" />
            Children Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-2 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isFullyCompliant = data?.overallPercentage === 100;
  const totalMissing = data?.totalItems - data?.completeItems || 0;

  return (
    <Card className="bg-white border shadow-sm" data-testid="children-comparison-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            {isFullyCompliant ? (
              <CheckCircle2 className="w-5 h-5 text-[#28a745]" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-[#ffc107]" />
            )}
            Children Records
          </CardTitle>
          <Button 
            onClick={fetchChildrenCompliance} 
            variant="ghost" 
            size="sm"
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {data?.overallPercentage || 0}% Compliance ({data?.completeItems || 0}/{data?.totalItems || 0} forms)
          </p>
          {totalMissing > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
              {totalMissing} Needed
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <Progress 
          value={data?.overallPercentage || 0} 
          className={`h-2 ${isFullyCompliant ? '' : '[&>div]:bg-yellow-500'}`}
        />
        
        {/* Category Breakdown */}
        <div className="space-y-2 border rounded p-2">
          {data?.categories && Object.entries(data.categories).map(([key, cat]) => (
            <div key={key} className="flex items-center justify-between py-1.5 border-b last:border-0">
              <div className="flex items-center gap-2">
                {getCategoryIcon(key)}
                <span className="text-sm font-medium">{cat.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-medium ${getCategoryColor(cat.percentage)}`}>
                  {cat.complete}/{cat.total}
                </span>
                {cat.expiring > 0 && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 text-[10px] px-1">
                    {cat.expiring} exp
                  </Badge>
                )}
                {cat.percentage === 100 ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Children List with Checkboxes - Collapsible */}
        {data?.childrenNeedingAttention?.length > 0 && (
          <div className="border rounded">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between p-2 hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {data.totalNeedingAttention} Children Need Attention
              </span>
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showDetails && (
              <div className="border-t">
                {/* Select All / Clear */}
                <div className="flex items-center justify-between text-xs px-2 py-1.5 bg-gray-50 border-b">
                  <span className="text-gray-500">Select children to email:</span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => toggleAllChildren(true)}
                      className="text-[#1a365d] hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={() => toggleAllChildren(false)}
                      className="text-gray-500 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                <div className="max-h-48 overflow-y-auto">
                  {data.childrenNeedingAttention.map((child, idx) => (
                    <div 
                      key={child.childId || idx}
                      className="p-2 border-b last:border-0 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`child-${child.childId}`}
                          checked={selectedChildren[child.childId] || false}
                          onCheckedChange={(checked) => setSelectedChildren(prev => ({...prev, [child.childId]: checked}))}
                          className="h-4 w-4"
                        />
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <label htmlFor={`child-${child.childId}`} className="text-sm font-medium cursor-pointer">{child.name}</label>
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1 rounded">
                          {child.category || 'Enrolled'}
                        </span>
                        {child.hasDocuments && (
                          <button
                            onClick={() => viewChildDocuments(child.childId, child.name)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 rounded hover:bg-blue-100 ml-auto"
                            title={`View ${child.documentCount} document(s)`}
                          >
                            <Eye className="w-3 h-3" />
                            View ({child.documentCount})
                          </button>
                        )}
                      </div>
                      {child.missing.length > 0 && (
                        <p className="text-[11px] text-red-600 ml-10 mt-0.5" data-testid={`missing-items-${child.childId}`}>
                          Missing: {child.missing.join(', ')}
                          {child.totalMissing > child.missing.length && ` +${child.totalMissing - child.missing.length} more`}
                        </p>
                      )}
                      {(child.expired || []).length > 0 && (
                        <p className="text-[11px] text-orange-600 ml-10 mt-0.5" data-testid={`expired-items-${child.childId}`}>
                          Expired: {child.expired.join(', ')}
                          {child.totalExpired > child.expired.length && ` +${child.totalExpired - child.expired.length} more`}
                        </p>
                      )}
                      {child.expiring.length > 0 && (
                        <p className="text-[11px] text-yellow-600 ml-10" data-testid={`expiring-items-${child.childId}`}>
                          Expiring: {child.expiring.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="flex gap-2">
          <Button 
            onClick={printChildrenReport} 
            disabled={printing}
            className="flex-1 bg-[#1a365d] hover:bg-[#2d4a7c] text-white"
          >
            <Printer className="w-4 h-4 mr-2" />
            {printing ? "Generating..." : "Print"}
          </Button>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={sendSelectedChildrenRecords}
                  disabled={sendingEmail || !inspectorEmail || Object.values(selectedChildren).filter(Boolean).length === 0}
                  variant="outline"
                  className="flex-1 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/10"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingEmail ? "Sending..." : `Email (${Object.values(selectedChildren).filter(Boolean).length})`}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs bg-white border shadow-lg p-3">
                {!inspectorEmail ? (
                  <p className="text-gray-600 text-xs">Enter inspector email below first</p>
                ) : Object.values(selectedChildren).filter(Boolean).length === 0 ? (
                  <p className="text-gray-600 text-xs">Select children to email</p>
                ) : (
                  <div className="text-xs">
                    <p className="font-semibold text-gray-800 mb-2">📎 Children documents to attach:</p>
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                      {getSelectedChildrenDocsList().map((doc, i) => (
                        <li key={i} className="text-gray-600">
                          <span className="font-medium text-gray-800">{doc.fileName}</span>
                          <br /><span className="text-[10px] text-gray-400">{doc.childName} - {doc.formType}</span>
                        </li>
                      ))}
                      {getSelectedChildrenDocsList().length === 0 && (
                        <li className="text-amber-600">No documents found for selected children</li>
                      )}
                    </ul>
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChildrenComparisonCard;
